import {LookupService} from "../common/api.service";
import {canViewCourseCatalog} from "./courseAccess";

const defaultStatusValidator = (response) => response?.status === 200;
const REGION_LIST_CACHE_TTL_MS = 5 * 60 * 1000;
let regionListCacheValue = null;
let regionListCacheExpiresAt = 0;
let regionListCachePromise = null;

// Centralized lookup handler to keep API calls consistent and error handling uniform.
const handleLookupRequest = async (requestFn, errorMessage, options = {}) => {
    const {
        validate = defaultStatusValidator,
        dataSelector = (response) => response.data,
    } = options;

    try {
        const response = await requestFn();
        if (validate(response)) {
            return dataSelector(response);
        }
        alert(errorMessage);
    } catch (error) {
        console.error(error);
        alert(errorMessage);
    }
    return undefined;
};

export const getUserToken = function () {
    const expirationTime = localStorage.getItem("expirationTime");
    if (expirationTime == null || Date.now() > expirationTime) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        return null;
    }
    return localStorage.getItem("token") || null;
};

export const setUserToken = function (token) {
    localStorage.setItem("token", token);
};
export const setExpirationTime = function (expirationTime) {
    localStorage.setItem("expirationTime", expirationTime);
};

export const getUser = function () {
    return localStorage.getItem("user") ? JSON.parse(localStorage.getItem("user")) : null;
};
export const getUserRole = function () {
    const user = localStorage.getItem("user") ? JSON.parse(localStorage.getItem("user")) : null;
    return user?.data?.userRoles?.roleName;
};

export const hasCertificateApprovalPermission = function (user = getUser()) {
    return normalizeRoleName(user?.data?.userRoles?.roleName) === "cpd leader ceo";
};

export const hasCertificateSigningPermission = function (user = getUser()) {
    return normalizeRoleName(user?.data?.userRoles?.roleName) === "cpd coordinator";
};

const normalizeRoleName = (value) => (value || "").toString().trim().toLowerCase();
const normalizeAccessValue = (value) =>
    (value || "")
        .toString()
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");

const OBSOLETE_REGION_NAMES = new Set([
    "snnp",
    "snnpr",
    "southernnationsnationalitiesandpeoplesregion",
]);

const filterCurrentRegions = (regions = []) =>
    regions.filter(
        (region) =>
            region?.isActive !== false &&
            !OBSOLETE_REGION_NAMES.has(normalizeAccessValue(region?.name))
    );

const normalizeRoutePath = (value) =>
    (value || "")
        .toString()
        .trim()
        .replace(/^\/+/, "")
        .replace(/\/+$/, "");

const MENU_ACCESS_ALIASES = {
    programeducation: ["programeducation", "educationalprograms", "programtype"],
    educationalprograms: ["programeducation", "educationalprograms", "programtype"],
    programtype: ["programeducation", "educationalprograms", "programtype"],
};

const SUPER_ADMIN_ROLE_NAMES = [
    "system admins",
    "system admin",
    "super admin",
    "super admins",
    "superadmin",
];

const PRESERVICE_ROLE_NAMES = [
    "pre-service admins",
    "pre-service university users",
];

const PRESERVICE_FALLBACK_MENU_CANDIDATES = [
    "ethnicity",
    "programeducation",
    "educationalprograms",
    "programtype",
];

const expandAccessCandidates = (candidates = []) => {
    const expanded = new Set();

    candidates.forEach((candidate) => {
        const normalizedCandidate = normalizeAccessValue(candidate);
        if (!normalizedCandidate) {
            return;
        }

        expanded.add(normalizedCandidate);
        (MENU_ACCESS_ALIASES[normalizedCandidate] || []).forEach((alias) => {
            expanded.add(alias);
        });
    });

    return [...expanded];
};

const SUPER_ADMIN_RESTRICTED_ACCESS_CANDIDATES = [
    "PreService",
    "/preservice",
    "Pre-service",
    "/pre-service",
    "TVET Program",
    "/pre-service/tvet-program",
    "Generic Entry",
    "/pre-service/generic-entry",
    "Post Basic",
    "/pre-service/post-basic",
    "Post Graduate",
    "/pre-service/post-graduate",
    "Graduate Entry",
    "/pre-service/graduate-entry",
    "Preservice Report",
    "/preserviceReport",
    "Universities",
    "/universities",
    "Program Education",
    "Education Program",
    "Educational Programs",
    "/educationalPrograms",
    "Disciplines",
    "/disciplines",
];

const SUPER_ADMIN_RESTRICTED_ACCESS_KEYS = expandAccessCandidates(
    SUPER_ADMIN_RESTRICTED_ACCESS_CANDIDATES
);

const ACCREDIT_COURSE_ROLE_NAMES = [
    "accredit course",
];

const ACCREDIT_COURSE_RESTRICTED_ACCESS_CANDIDATES = [
    "CPD Accreditor",
    "/accreditor",
    "/Accreditor",
];

const ACCREDIT_COURSE_RESTRICTED_ACCESS_KEYS = expandAccessCandidates(
    ACCREDIT_COURSE_RESTRICTED_ACCESS_CANDIDATES
);

const isSuperAdminRole = (roleName) =>
    SUPER_ADMIN_ROLE_NAMES.includes(normalizeRoleName(roleName));

const isRestrictedForSuperAdmin = (roleName, candidates = []) => {
    if (!isSuperAdminRole(roleName)) {
        return false;
    }

    const requestedCandidates = expandAccessCandidates(candidates);

    return requestedCandidates.some((candidate) =>
        SUPER_ADMIN_RESTRICTED_ACCESS_KEYS.includes(candidate)
    );
};

const isAccreditCourseRole = (roleName) =>
    ACCREDIT_COURSE_ROLE_NAMES.includes(normalizeRoleName(roleName));

const isRestrictedForAccreditCourseRole = (roleName, candidates = []) => {
    if (!isAccreditCourseRole(roleName)) {
        return false;
    }

    const requestedCandidates = expandAccessCandidates(candidates);

    return requestedCandidates.some((candidate) =>
        ACCREDIT_COURSE_RESTRICTED_ACCESS_KEYS.includes(candidate)
    );
};

const isRoleRestrictedAccess = (roleName, candidates = []) =>
    isRestrictedForSuperAdmin(roleName, candidates) ||
    isRestrictedForAccreditCourseRole(roleName, candidates);

const LOOKUP_MODE_ORDER = ["distance", "evening", "extension", "part-time", "regular", "summer", "weekend"];
const LOOKUP_POSTGRADUATE_ORDER = ["specialty", "sub-specialty", "super-specialty", "ultra-specialty"];
const POSTGRADUATE_PROGRAM_CODES = new Set([
    "SPECIALTY",
    "SUBSPECIALTY",
    "SUPERSPECIALTY",
    "ULTRASPECIALTY",
]);

const isActiveLookupRow = (row) => row?.isActive !== false;

const isPostgraduateEducationalProgram = (row = {}) => {
    const normalizedCode = (row?.code || "").toString().trim().toUpperCase();
    const normalizedName = (row?.name || "").toString().trim().toLowerCase();

    return POSTGRADUATE_PROGRAM_CODES.has(normalizedCode) || LOOKUP_POSTGRADUATE_ORDER.includes(normalizedName);
};

const filterEducationalProgramsByScope = (rows = [], scope = "all") => {
    if (scope === "postgraduate") {
        return rows.filter((row) => isPostgraduateEducationalProgram(row));
    }

    if (scope === "standard") {
        return rows.filter((row) => !isPostgraduateEducationalProgram(row));
    }

    return rows;
};

const filterDisciplinesByScope = (rows = [], scope = "all") => {
    if (scope === "all") {
        return rows;
    }

    return rows.filter((row) => {
        const belongsToPostgraduateProgram = isPostgraduateEducationalProgram(row?.educationProgram);
        return scope === "postgraduate" ? belongsToPostgraduateProgram : !belongsToPostgraduateProgram;
    });
};

const filterDisciplinesByLevelType = (rows = [], levelType = null) => {
    const normalizedLevelType = (levelType || "").toString().trim().toLowerCase();
    if (!normalizedLevelType) {
        return rows;
    }

    return rows.filter((row) => {
        const configuredLevelTypes = Array.isArray(row?.levelTypes)
            ? row.levelTypes
                .map((value) => (value || "").toString().trim().toLowerCase())
                .filter(Boolean)
            : [];

        // Rows created before level scoping was introduced remain available until an
        // administrator explicitly scopes them.
        return configuredLevelTypes.length === 0 || configuredLevelTypes.includes(normalizedLevelType);
    });
};

const sortEducationalPrograms = (rows = []) =>
    [...rows].sort((left, right) => {
        const leftName = (left?.name || "").toString().trim();
        const rightName = (right?.name || "").toString().trim();
        const leftIndex = LOOKUP_MODE_ORDER.indexOf(leftName.toLowerCase());
        const rightIndex = LOOKUP_MODE_ORDER.indexOf(rightName.toLowerCase());
        const leftPostgraduateIndex = LOOKUP_POSTGRADUATE_ORDER.indexOf(leftName.toLowerCase());
        const rightPostgraduateIndex = LOOKUP_POSTGRADUATE_ORDER.indexOf(rightName.toLowerCase());

        if (leftIndex !== -1 || rightIndex !== -1) {
            if (leftIndex === -1) return 1;
            if (rightIndex === -1) return -1;
            return leftIndex - rightIndex;
        }

        if (leftPostgraduateIndex !== -1 || rightPostgraduateIndex !== -1) {
            if (leftPostgraduateIndex === -1) return 1;
            if (rightPostgraduateIndex === -1) return -1;
            return leftPostgraduateIndex - rightPostgraduateIndex;
        }

        return leftName.localeCompare(rightName);
    });

const filterActiveLookupResponse = (response, sorter = (rows) => rows) => {
    const rows = Array.isArray(response?.data?.rows)
        ? response.data.rows.filter(isActiveLookupRow)
        : [];

    return {
        ...response.data,
        count: rows.length,
        rows: sorter(rows),
    };
};

const REQUIRED_PRESERVICE_STUDENT_STATUSES = [
    {name: "New", code: "NEW"},
    {name: "Transfer", code: "TRANSFER"},
    {name: "Withdrawn", code: "WITHDRAWN"},
];

const ensurePreserviceStudentStatusOptions = (rows = []) => {
    const normalizedRows = Array.isArray(rows) ? [...rows] : [];
    const existingNames = new Set(
        normalizedRows.map((row) => normalizeAccessValue(row?.name))
    );

    REQUIRED_PRESERVICE_STUDENT_STATUSES.forEach((status) => {
        if (!existingNames.has(normalizeAccessValue(status.name))) {
            normalizedRows.push({
                id: status.name.toLowerCase(),
                ...status,
                isActive: true,
            });
        }
    });

    return normalizedRows.sort((left, right) =>
        (left?.name || "").toString().localeCompare((right?.name || "").toString())
    );
};

export const hasPermissionMatch = function (permission, candidates = []) {
    if (!permission?.canRead) {
        return false;
    }

    const permissionCandidates = expandAccessCandidates([
        permission?.menuItem?.menuItemName,
        normalizeRoutePath(permission?.menuItem?.url),
    ]);
    const requestedCandidates = expandAccessCandidates(candidates);

    return requestedCandidates.some((candidate) => permissionCandidates.includes(candidate));
};

export const canAccessRoute = function (user = getUser(), route = {}) {
    const routeCandidates = [
        route?.name,
        normalizeRoutePath(route?.path),
        ...(Array.isArray(route?.accessAliases) ? route.accessAliases : []),
        ...(Array.isArray(route?.meta?.accessAliases) ? route.meta.accessAliases : []),
    ].filter(Boolean);

    const roleName = normalizeRoleName(user?.data?.userRoles?.roleName);
    if (isRoleRestrictedAccess(roleName, routeCandidates)) {
        return false;
    }

    if (canViewCourseCatalog(user) && routeCandidates.some(
        (candidate) => ["course", "courses"].includes(normalizeAccessValue(candidate))
    )) return true;

    const hasPreserviceFallbackAccess =
        PRESERVICE_ROLE_NAMES.includes(roleName) &&
        routeCandidates.some((candidate) =>
            PRESERVICE_FALLBACK_MENU_CANDIDATES.includes(normalizeAccessValue(candidate))
        );

    if (hasPreserviceFallbackAccess) {
        return true;
    }

    const hasCpdLeadershipDashboardAccess =
        ["cpd coordinator", "cpd director", "cpd leader ceo"].includes(roleName) &&
        routeCandidates.some((candidate) => normalizeAccessValue(candidate) === "dashboard");

    if (hasCpdLeadershipDashboardAccess) {
        return true;
    }

    return (user?.permissions || []).some((permission) =>
        hasPermissionMatch(permission, routeCandidates)
    );
};

export const canAccessMenuItem = function (user = getUser(), item = {}) {
    const menuCandidates = [
        item?.title,
        normalizeRoutePath(item?.to),
        ...(Array.isArray(item?.accessAliases) ? item.accessAliases : []),
    ].filter(Boolean);

    const roleName = normalizeRoleName(user?.data?.userRoles?.roleName);
    if (isRoleRestrictedAccess(roleName, menuCandidates)) {
        return false;
    }

    if (canViewCourseCatalog(user) && menuCandidates.some(
        (candidate) => ["course", "courses"].includes(normalizeAccessValue(candidate))
    )) return true;

    const hasPreserviceFallbackAccess =
        PRESERVICE_ROLE_NAMES.includes(roleName) &&
        menuCandidates.some((candidate) =>
            PRESERVICE_FALLBACK_MENU_CANDIDATES.includes(normalizeAccessValue(candidate))
        );

    if (hasPreserviceFallbackAccess) {
        return true;
    }

    const hasCpdLeadershipDashboardAccess =
        ["cpd coordinator", "cpd director", "cpd leader ceo"].includes(roleName) &&
        menuCandidates.some((candidate) => normalizeAccessValue(candidate) === "dashboard");

    if (hasCpdLeadershipDashboardAccess) {
        return true;
    }

    return (user?.permissions || []).some((permission) =>
        hasPermissionMatch(permission, menuCandidates)
    );
};

export const getDefaultRouteForUser = function (user = getUser()) {
    const roleName = normalizeRoleName(user?.data?.userRoles?.roleName);
    if (roleName.includes("pre-service") || roleName.includes("preservice")) {
        return "/preservice";
    }

    const firstReadablePermission = (user?.permissions || []).find(
        (permission) =>
            permission?.canRead === true &&
            permission?.menuItem?.url &&
            !isRoleRestrictedAccess(roleName, [
                permission?.menuItem?.menuItemName,
                permission?.menuItem?.url,
            ])
    );

    if (firstReadablePermission?.menuItem?.url) {
        return `/${firstReadablePermission.menuItem.url}`;
    }

    return "/dashboard";
};

export const isUniversityScopedPreserviceUser = function (user = getUser()) {
    return normalizeRoleName(user?.data?.userRoles?.roleName) === "pre-service university users";
};

export const getAssignedUniversityOption = function (user = getUser()) {
    const university = user?.data?.educationalInstitution;
    const universityId = user?.data?.eduInstitutionId;

    if (!universityId || !university?.name) {
        return null;
    }

    return {
        ...university,
        id: Number(universityId),
        name: university.name,
    };
};

export {
    CERTIFICATE_APPROVAL_STATUS,
    isCertificateApprovalPending,
    isCertificatePendingCoordinatorSignature,
    isCertificateApprovalComplete,
    isCertificateUnapproved,
    TRAINEE_STATUS_FILTERS,
    traineeStatusQuery,
} from "./certificateApproval";

export const setUser = function (data) {
    localStorage.setItem("user", JSON.stringify(data));
};

export const getGraduateTypes = async function () {
    return handleLookupRequest(
        () => LookupService.getGraduateTypes(),
        "Error while fetching Graduate Types"
    );
};
export const getTrainingCenters = async function () {
    return handleLookupRequest(
        () => LookupService.getTrainingCenters(),
        "Error while fetching CPD providers"
    );
};

export const getCatIIRequirements = async function (params = {}) {
    const cpdCategoryId = params?.cpdCategoryId ? Number(params.cpdCategoryId) : null;
    return handleLookupRequest(
        () =>
            cpdCategoryId
                ? LookupService.getCatIIRequirementsByCategory(cpdCategoryId)
                : LookupService.getCatIIRequirements(),
        "Error while fetching Cat II requirements"
    );
};

export const getHealthProfessions = async function () {
    return handleLookupRequest(
        () => LookupService.getHealthProffesions(),
        "Error while fetching Health Professions"
    );
};

export const getCourses = async function () {
    return handleLookupRequest(
        () => LookupService.getCourses(),
        "Error while fetching course list",
        {
            validate: (response) => response?.data?.status === 200,
            dataSelector: (response) => response.data.data,
        }
    );
};
export const getProgramTypes = async function () {
    return handleLookupRequest(
        () => LookupService.getProgramTypes(),
        "Error while fetching program types"
    );
};
export const getCourseLevel = async function () {
    return handleLookupRequest(
        () => LookupService.getCourseLevel(),
        "Error while fetching course level"
    );
};
export const getTrainerTypes = async function () {
    return handleLookupRequest(
        () => LookupService.getTrainerTypes(),
        "Error while fetching trainer types"
    );
};
export const getPrograms = async function () {
    return handleLookupRequest(
        () => LookupService.getPrograms(),
        "Error while fetching programs"
    );
};
export const getProgramsLite = async function () {
    return handleLookupRequest(
        () => LookupService.getProgramsLite(),
        "Error while fetching programs"
    );
};
export const getProgramsDrp = async function (id) {
    return handleLookupRequest(
        () => LookupService.getProgramsDrp(id),
        "Error while fetching programs"
    );
};
export const getTrainers = async function () {
    return handleLookupRequest(
        () => LookupService.getTrainers(),
        "Error while fetching trainers"
    );
};
export const getTrainees = async function () {
    return handleLookupRequest(
        () => LookupService.getTrainees(),
        "Error while fetching trainees"
    );
};
export const getProfessions = async function () {
    return handleLookupRequest(
        () => LookupService.getProfession(),
        "Error while fetching profession"
    );
};
export const getEducationalLevel = async function () {
    return handleLookupRequest(
        () => LookupService.getEducationalLevel(),
        "Error while fetching educational levels"
    );
};
export const getCategories = async function () {
    return handleLookupRequest(
        () => LookupService.getCategories(),
        "Error while fetching categories"
    );
};
export const getRegionList = async function () {
    const now = Date.now();
    if (regionListCacheValue && regionListCacheExpiresAt > now) {
        return regionListCacheValue;
    }

    if (regionListCachePromise) {
        return regionListCachePromise;
    }

    regionListCachePromise = handleLookupRequest(
        () => LookupService.getRegion(),
        "Error while fetching regions"
    ).then((regions) => {
        regionListCachePromise = null;
        if (Array.isArray(regions)) {
            regionListCacheValue = filterCurrentRegions(regions);
            regionListCacheExpiresAt = Date.now() + REGION_LIST_CACHE_TTL_MS;
        }
        return Array.isArray(regions) ? filterCurrentRegions(regions) : regions;
    });

    return regionListCachePromise;
};
export const getTrainerSelectionCriteriaList = async function () {
    return handleLookupRequest(
        () => LookupService.getTrainerSelectionCriteria(),
        "Error while fetching Trainer Selection Criteria"
    );
};
export const getWorkExperienceList = async function () {
    return handleLookupRequest(
        () => LookupService.getWorkExperience(),
        "Error while fetching Work Experience"
    );
};
export const getTrainingCategories = async function () {
    return handleLookupRequest(
        () => LookupService.getTrainingCategories(),
        "Error while fetching Work Training Categories"
    );
};
export const getLevelOfStudies = async function () {
    return handleLookupRequest(
        () => LookupService.getLevelOfStudies(),
        "Error while fetching Level of Studies",
        {
            dataSelector: (response) => filterActiveLookupResponse(response),
        }
    );
};
export const getAccreditedBy = async function () {
    return handleLookupRequest(
        () => LookupService.getAccreditedBy(),
        "Error while fetching Accredited by"
    );
};
export const getCourseOwners = async function () {
    return handleLookupRequest(
        () => LookupService.getCourseOwners(),
        "Error while fetching Course Owners"
    );
};
export const getAudiences = async function () {
    return handleLookupRequest(
        () => LookupService.getAudiences(),
        "Error while fetching Target Audiences"
    );
};
export const getTrainingCourseUsed = async function () {
    return handleLookupRequest(
        () => LookupService.getTrainingCourseUsed(),
        "Error while fetching Training Course Used"
    );
};
export const getFoundedBy = async function () {
    return handleLookupRequest(
        () => LookupService.getFoundedBy(),
        "Error while fetching Founded By"
    );
};
export const getActivityDeliveries = async function () {
    return handleLookupRequest(
        () => LookupService.getActivityDeliveries(),
        "Error while fetching Founded By"
    );
};
export const getCpdProviders = async function () {
    return handleLookupRequest(
        () => LookupService.getCpdProviders(),
        "Error while fetching CPD providers"
    );
};
export const getZoneByRegionId = async function (regionId) {
    return handleLookupRequest(
        () => LookupService.getZoneByRegionId(regionId),
        "Error while fetching zones"
    );
};
export const getZoneList = async function () {
    return handleLookupRequest(
        () => LookupService.getZones(),
        "Error while fetching zones"
    );
};
export const getWoredaByZoneId = async function (zoneId) {
    return handleLookupRequest(
        () => LookupService.getWoredaByZoneId(zoneId),
        "Error while fetching zones"
    );
};
export const getFacilitiesByWoredaId = async function (woredaId) {
    return handleLookupRequest(
        () => LookupService.getFacilityByWoredaId(woredaId),
        "Error while fetching facilities"
    );
};
export const getWoredaList = async function () {
    return handleLookupRequest(
        () => LookupService.getWoredas(),
        "Error while fetching woredas"
    );
};
export const getExpertLevels = async function () {
    return handleLookupRequest(
        () => LookupService.getExpertLevels(),
        "Error while fetching expert levels"
    );
};
export const getFacilityTypes = async function () {
    return handleLookupRequest(
        () => LookupService.getFacilityTypes(),
        "Error while fetching facility types"
    );
};
export const getUserFacilities = async function (locationId, facilityTypeId) {
    return handleLookupRequest(
        () => LookupService.getUserFacilities(locationId, facilityTypeId),
        "Error while fetching Facilities",
        {
            dataSelector: (response) => response.data?.data,
        }
    );
};
export const getAccredatorOwnership = async function () {
    return handleLookupRequest(
        () => LookupService.getAccredatorOwnership(),
        "Error while fetching accreditor ownerships"
    );
};
export const getTraineeFacilities = async function () {
    return handleLookupRequest(
        () => LookupService.getTraineeFacilities(),
        "Error while fetching new trainee Facilities."
    );
};
export const getUniversityOwnership = async function () {
    return handleLookupRequest(
        () => LookupService.getUniversityOwnership(),
        "Error while fetching university ownerships"
    );
};
export const getGenerationLevels = async function () {
    return handleLookupRequest(
        () => LookupService.getGenerationLevels(),
        "Error while fetching generation levels"
    );
};

export const getUniversities = async function () {
    return handleLookupRequest(
        () => LookupService.getUniversities(),
        "Error while fetching universities"
    );
};

export const getCertReason = async function () {
    return handleLookupRequest(
        () => LookupService.getCertReasons(),
        "Error while fetching certificate reasons"
    );
};

export const getStudentStatuses = async function () {
    return handleLookupRequest(
        () => LookupService.getStudentStatuses(),
        "Error while fetching student statuses",
        {
            dataSelector: (response) => {
                const rows = Array.isArray(response?.data?.rows)
                    ? response.data.rows.filter(isActiveLookupRow)
                    : Array.isArray(response?.data)
                    ? response.data.filter(isActiveLookupRow)
                    : [];

                return ensurePreserviceStudentStatusOptions(rows);
            },
        }
    );
};

export const getEthnicities = async function () {
    return handleLookupRequest(
        () => LookupService.getEthnicities(),
        "Error while fetching ethnicities",
        {
            dataSelector: (response) => {
                const rows = Array.isArray(response?.data?.rows)
                    ? response.data.rows.filter(isActiveLookupRow)
                    : Array.isArray(response?.data)
                    ? response.data.filter(isActiveLookupRow)
                    : [];

                return {
                    rows,
                    count: rows.length,
                };
            },
        }
    );
};

export const getEducationalPrograms = async function ({scope = "all"} = {}) {
    return handleLookupRequest(
        () => LookupService.getEducationalPrograms(),
        "Error while fetching educational programs",
        {
            dataSelector: (response) =>
                filterActiveLookupResponse(response, (rows) =>
                    sortEducationalPrograms(filterEducationalProgramsByScope(rows, scope))
                ),
        }
    );
};

export const getDisciplines = async function ({scope = "all", levelType = null} = {}) {
    return handleLookupRequest(
        () => LookupService.getDisciplines(),
        "Error while fetching disciplines",
        {
            dataSelector: (response) =>
                filterActiveLookupResponse(response, (rows) =>
                    filterDisciplinesByLevelType(
                        filterDisciplinesByScope(rows, scope),
                        levelType
                    )
                ),
        }
    );
};
